import omit from 'lodash/omit'
import orderBy from 'lodash/orderBy'

import api from 'api'
import {
  CACHE_TAG_DAILY_ROUTE_VERSIONS,
  CACHE_TAG_ROUTES,
  CACHE_TAG_ROUTES_STOPS,
  CACHE_TAG_ROUTES_GEOJSON,
  CACHE_TAG_ROUTE_STOP_LISTS,
} from 'api/cacheTagTypes'

import type { VersionDetailsEntry } from 'api/route/masterRoute'
import type { BulkChangeDateBody } from 'types/api/route/bulkRequests'
import type { DailyStopApi } from 'types/api/route/dailyStop'
import type {
  AssignDailyStopsBody,
  AssignDailyStopsResponse,
  ChangeDateDailyStopsBody,
  ChangeDateResponse,
  DailySequenceResponse,
  RestoreDailyVersionBody,
  RestoreDailyVersionResponse,
  SyncDailyOrderToMasterBody,
  SyncDailyOrderToMasterResponse,
  UnassignDailyStopsBody,
  UnassignDailyStopsResponse,
  UpdateDailySequenceBody,
} from 'types/api/route/dailyStopRequests'
import type { GetStopDetailsBody, StopSequence } from 'types/api/route/masterStopRequests'

export const DAILY_INVALIDATION_TAGS = [
  CACHE_TAG_ROUTES,
  CACHE_TAG_ROUTES_STOPS,
  CACHE_TAG_ROUTES_GEOJSON,
  CACHE_TAG_DAILY_ROUTE_VERSIONS,
] as const

/**
 * Conditional `invalidatesTags` shared by every daily mutation. Mirrors the
 * pattern in `masterRoute.ts` so the v2 executor's `skipInvalidate: true`
 * contract is honored on daily endpoints too.
 */
export const dailyInvalidatesTags = (_result: unknown, _error: unknown, arg: { skipInvalidate?: boolean } | undefined): readonly string[] =>
  arg?.skipInvalidate ? [] : [...DAILY_INVALIDATION_TAGS]

interface RouteIdArg {
  routeId: string
}

interface SkipInvalidateArg {
  skipInvalidate?: boolean
}

interface GetDailyStopsArgs extends RouteIdArg {
  serviceDate: string
  includeTags?: boolean
  includeContainers?: boolean
}

interface GetDailyStopsDetailsArgs extends RouteIdArg {
  includeTags?: boolean
  includeContainers?: boolean
  data: GetStopDetailsBody
}

interface UpdateDailySequenceArgs extends RouteIdArg, SkipInvalidateArg {
  data: UpdateDailySequenceBody
}

interface UnassignDailyStopsArgs extends RouteIdArg, SkipInvalidateArg {
  data: UnassignDailyStopsBody
}

interface AssignDailyStopsArgs extends RouteIdArg, SkipInvalidateArg {
  data: AssignDailyStopsBody
}

interface ChangeDateDailyStopsArgs extends RouteIdArg, SkipInvalidateArg {
  data: ChangeDateDailyStopsBody
}

interface BulkChangeDateArgs extends BulkChangeDateBody, SkipInvalidateArg {}

interface RestoreDailyVersionArgs extends RouteIdArg, SkipInvalidateArg {
  data: RestoreDailyVersionBody
}

interface SyncDailyOrderToMasterArgs extends RouteIdArg, SkipInvalidateArg {
  data: SyncDailyOrderToMasterBody
}

interface GetDailyStopsVersionsArgs extends RouteIdArg {
  serviceDate: string
}

interface GetDailyStopsVersionsLatestDetailsArgs extends RouteIdArg {
  serviceDates: ReadonlyArray<string>
}

export interface DailyStopsResponse {
  stops: DailyStopApi[]
}

export interface DailyStopsVersionsResponse {
  versions: ReadonlyArray<{
    id: string
    rev: number
    createdBy: string
    createdAt: string
    createdByUser: unknown
    sequence: StopSequence[]
  }>
}

// BE returns Map<LocalDate, RouteStopVersion>; dates with no version are omitted.
export type DailyStopsVersionsLatestDetailsResponse = Record<string, VersionDetailsEntry>

const extendedApi = api.injectEndpoints({
  endpoints: builder => ({
    getDailyStops: builder.query<DailyStopsResponse, GetDailyStopsArgs>({
      query: ({ routeId, serviceDate, includeTags = true, includeContainers = false }) => ({
        url: `/api/v2/route/${routeId}/daily/stops`,
        method: 'GET',
        params: { serviceDate, includeTags, includeContainers },
      }),
      // Mirrors getMasterStops — see there for why structural sharing is off.
      structuralSharing: false,
      providesTags: [CACHE_TAG_ROUTES_STOPS, CACHE_TAG_ROUTE_STOP_LISTS],
    }),
    getDailyStopsDetails: builder.query<DailyStopsResponse, GetDailyStopsDetailsArgs>({
      query: ({ routeId, includeTags = true, includeContainers = false, data }) => ({
        url: `/api/v2/route/${routeId}/daily/stops/get/details`,
        method: 'POST',
        params: { includeTags, includeContainers },
        body: data,
      }),
      providesTags: [CACHE_TAG_ROUTES_STOPS, CACHE_TAG_ROUTE_STOP_LISTS],
    }),
    updateDailySequence: builder.mutation<DailySequenceResponse, UpdateDailySequenceArgs>({
      query: ({ routeId, data }) => ({
        url: `/api/v2/route/${routeId}/daily/stops/sequence`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: dailyInvalidatesTags,
    }),
    unassignDailyStops: builder.mutation<UnassignDailyStopsResponse, UnassignDailyStopsArgs>({
      query: ({ routeId, data }) => ({
        url: `/api/v2/route/${routeId}/daily/stops/unassign`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: dailyInvalidatesTags,
    }),
    assignDailyStops: builder.mutation<AssignDailyStopsResponse, AssignDailyStopsArgs>({
      query: ({ routeId, data }) => ({
        url: `/api/v2/route/${routeId}/daily/stops/assign`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: dailyInvalidatesTags,
    }),
    changeDateDailyStops: builder.mutation<ChangeDateResponse, ChangeDateDailyStopsArgs>({
      query: ({ routeId, data }) => ({
        url: `/api/v2/route/${routeId}/daily/stops/change-date`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: dailyInvalidatesTags,
    }),
    // v1 — full-move (updates wo.serviceDate + route_daily_sequence + master sequence).
    // Use this for between-days moves until v2 change-date migrates the daily sequence row too.
    bulkChangeDate: builder.mutation<unknown, BulkChangeDateArgs>({
      query: body => ({
        url: '/api/v1/core/route/stops/change-date/bulk',
        method: 'POST',
        body: omit(body, ['skipInvalidate']),
      }),
      invalidatesTags: dailyInvalidatesTags,
    }),
    restoreDailyVersion: builder.mutation<RestoreDailyVersionResponse, RestoreDailyVersionArgs>({
      query: ({ routeId, data }) => ({
        url: `/api/v2/route/${routeId}/daily/stops/restore`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: dailyInvalidatesTags,
    }),
    // Preview is read-only — same body as the committing mutation, no cache
    // invalidation. Caller joins the returned IDs against daily details to
    // render cards (see `useRestoreDailyVersionPreview`).
    previewRestoreDailyVersion: builder.mutation<DailySequenceResponse, RestoreDailyVersionArgs>({
      query: ({ routeId, data }) => ({
        url: `/api/v2/route/${routeId}/daily/stops/restore/preview`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: [],
    }),
    syncDailyOrderToMaster: builder.mutation<SyncDailyOrderToMasterResponse, SyncDailyOrderToMasterArgs>({
      query: ({ routeId, data }) => ({
        url: `/api/v2/route/${routeId}/daily/stops/sync-to-master`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: dailyInvalidatesTags,
    }),
    getDailyStopsVersions: builder.query<DailyStopsVersionsResponse, GetDailyStopsVersionsArgs>({
      query: ({ routeId, serviceDate }) => ({
        url: `/api/v2/route/${routeId}/daily/stops/versions`,
        method: 'GET',
        params: { serviceDate },
      }),
      // Order by (rev DESC, createdAt DESC) so the activity panel renders newest first
      // and same-rev rows are tie-broken deterministically by createdAt. Sorting here
      // (instead of in the consuming component) means every consumer of this query
      // gets the canonical order automatically.
      transformResponse: (response: DailyStopsVersionsResponse) => ({
        ...response,
        versions: orderBy(response.versions, ['rev', 'createdAt'], ['desc', 'desc']),
      }),
      providesTags: [CACHE_TAG_DAILY_ROUTE_VERSIONS],
    }),
    getDailyStopsVersionsLatestDetails: builder.query<DailyStopsVersionsLatestDetailsResponse, GetDailyStopsVersionsLatestDetailsArgs>({
      query: ({ routeId, serviceDates }) => ({
        url: `/api/v2/route/${routeId}/daily/stops/versions/latest/details`,
        method: 'GET',
        params: { serviceDates },
      }),
      providesTags: [CACHE_TAG_DAILY_ROUTE_VERSIONS],
    }),
  }),
})

export const {
  useGetDailyStopsQuery,
  useLazyGetDailyStopsQuery,
  useLazyGetDailyStopsDetailsQuery,
  useUpdateDailySequenceMutation,
  useUnassignDailyStopsMutation,
  useAssignDailyStopsMutation,
  useChangeDateDailyStopsMutation,
  useBulkChangeDateMutation,
  useRestoreDailyVersionMutation,
  usePreviewRestoreDailyVersionMutation,
  useSyncDailyOrderToMasterMutation,
  useLazyGetDailyStopsVersionsQuery,
  useLazyGetDailyStopsVersionsLatestDetailsQuery,
} = extendedApi
